import { SubmitErrorHandler, SubmitHandler } from "react-hook-form";

import invariant from "tiny-invariant";

import { useTournamentContext } from "../contexts/Tournament";
import { OnChangeChildBracketTypeProps } from "../types/tournament";

import { store } from "../../../../..";
import { alertActions } from "../../../../../features/alert/alertSlice";
import { selectBracketTypes } from "../../../../../features/global/globalSlice";
import {
    selectSelectedTournament,
    tournamentManagementActions,
} from "../../../../../features/tourneymgmt/tournamentManagementSlice";
import {
    useAppDispatch,
    useAppSelector,
} from "../../../../../hooks/reduxHooks";
import {
    createTournament,
    getTournamentSchedule,
    updateTournament,
} from "../../../../../lib/api";
import { isChildTournament } from "../../../../../lib/utils";
import {
    IGame,
    IMasterTournamentSchedule,
    ITournamentSchedule,
} from "../../../../../types/api";
import { ITourneyMgmtTourneyInfoData } from "../../../../../types/forms";

export const useFormHandlers = () => {
    const {
        isCreatingTournament,
        logo,
        photos,
        currentPhotos,
        tournament,
        setCurrentPhotos,
        setPhotos,
        modalOldBracketGames,
        setModalOldBracketGames,
        modalDroppedGames,
        setModalDropGamesCount,
        setFetchStatus,
    } = useTournamentContext();
    const dispatch = useAppDispatch();
    const user = useAppSelector(state => state.auth.user);
    const brackets = useAppSelector(selectBracketTypes);

    invariant(user);

    const extractGames = (
        schedule: ITournamentSchedule | IMasterTournamentSchedule,
    ): IGame[] => {
        if (!schedule.games || schedule.games.length === 0) return [];

        if ("id" in schedule.games[0]) {
            return (schedule as ITournamentSchedule).games as IGame[];
        } else {
            return (schedule as IMasterTournamentSchedule).games.flatMap(
                group => group.games ?? [],
            );
        }
    };

    const onSubmit: SubmitHandler<ITourneyMgmtTourneyInfoData> = async data => {
        setFetchStatus("pending");
        data.logo = logo;
        data.photos = photos;

        !isCreatingTournament && (data.currentPhotos = currentPhotos);

        const tournamentsPromise = isCreatingTournament
            ? createTournament(data, user.id)
            : updateTournament(tournament!.id, data, modalDroppedGames);

        const isMasterTournament = !isChildTournament(tournament);

        try {
            const response = await tournamentsPromise;

            const alertAction = isCreatingTournament ? "created" : "updated";

            isMasterTournament
                ? dispatch(
                      tournamentManagementActions.setMasterTournaments(
                          response.tournaments,
                      ),
                  )
                : dispatch(
                      tournamentManagementActions.setTournaments(
                          response.tournaments,
                      ),
                  );

            !isCreatingTournament &&
                dispatch(
                    tournamentManagementActions.setselectedTournamentId(
                        tournament?.id,
                    ),
                );

            const thisTournament = selectSelectedTournament(store.getState());

            thisTournament &&
                !isChildTournament(thisTournament) &&
                thisTournament.photos &&
                setCurrentPhotos(thisTournament.photos);

            setPhotos([]);
            setModalOldBracketGames([]);

            dispatch(
                alertActions.success({
                    title: `Tournament ${alertAction} successfully`,
                    dismissible: true,
                }),
            );
            setFetchStatus("success");
        } catch (error: any) {
            const alertAction = isCreatingTournament ? "creating" : "updating";

            dispatch(
                alertActions.error({
                    title: `The server encountered a problem ${alertAction} the tournament.`,
                    text: "If the problem continues, please reach out to an admin.",
                    dismissible: true,
                }),
            );
            setFetchStatus("failed");
        } finally {
            setFetchStatus(undefined);
        }
    };

    const onError: SubmitErrorHandler<
        ITourneyMgmtTourneyInfoData
    > = async data => {
        console.log(data);
    };

    const onChangeChildBracketType = async (
        props: OnChangeChildBracketTypeProps,
    ): Promise<boolean> => {
        const { newValue, originalBracket, tournamentId } = props;
        if (newValue) {
            const currentBracket = brackets.find(
                b => b.bracketId === originalBracket,
            );
            const newBracket = brackets.find(
                b => b.bracketId === newValue.value,
            );

            const currentGameCount =
                (currentBracket?.games.championship.length ?? 0) +
                (currentBracket?.games.consolation?.length ?? 0);
            const newGameCount =
                (newBracket?.games.championship.length ?? 0) +
                (newBracket?.games.consolation?.length ?? 0);

            if (newGameCount < currentGameCount) {
                try {
                    if (modalOldBracketGames.length === 0) {
                        const response =
                            await getTournamentSchedule(tournamentId);

                        const games = extractGames(response.schedule);
                        const bracketGames =
                            games.filter(g => g.isPlayoff === 1) || [];

                        if (bracketGames.length === 0) {
                            return false;
                        }

                        setModalOldBracketGames(bracketGames);
                    }
                } catch (error: any) {
                    let errorMessage: string;
                    if (error instanceof Error) {
                        errorMessage = error.message;
                        console.log(error.message);
                    } else if (typeof error === "string") {
                        errorMessage = error;
                    } else {
                        errorMessage = "An unexpected error occurred";
                    }

                    dispatch(
                        alertActions.error({
                            title: "Error fetching tournament schedule",
                            text: errorMessage,
                            dismissible: true,
                        }),
                    );
                }

                setModalDropGamesCount(currentGameCount - newGameCount);
                return true;
            }
        }

        return false;
    };

    return { onSubmit, onError, onChangeChildBracketType };
};
